import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import DeckleImage from '@/components/generals/deckle-image'
import { ghostButton, primaryButton } from '@/components/generals/button-styles'

const HomeHero: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative overflow-hidden'>
            <div className='container-page grid gap-12 pb-20 pt-10 md:pt-16 lg:grid-cols-12 lg:gap-8 lg:pb-32 lg:pt-14'>
                <div className='flex flex-col lg:col-span-5 lg:pt-28'>
                    <p className='animate-in fade-in slide-in-from-bottom-2 flex items-center gap-4 text-sm font-medium text-bark-soft duration-1000 ease-out motion-reduce:animate-none'>
                        <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                        Samarkand silk paper
                    </p>

                    <h1
                        id='hero-title'
                        className='animate-in fade-in slide-in-from-bottom-4 mt-7 font-heading text-[clamp(3.1rem,7.2vw,6.4rem)] font-light leading-[0.95] tracking-[-0.025em] text-bark delay-150 duration-[1400ms] ease-out fill-mode-both motion-reduce:animate-none'
                    >
                        A thousand years old,{' '}
                        <span className='block'>made this morning.</span>
                    </h1>

                    <p className='animate-in fade-in mt-8 max-w-[26rem] text-[1.05rem] leading-relaxed text-bark-soft delay-500 duration-[1400ms] ease-out fill-mode-both motion-reduce:animate-none'>
                        Sheets, notebooks and wedding stationery formed from mulberry bark at our water mill outside Samarkand, for calligraphers, artists and designers everywhere.
                    </p>

                    <div className='animate-in fade-in mt-10 flex flex-wrap gap-3 delay-700 duration-[1400ms] ease-out fill-mode-both motion-reduce:animate-none'>
                        <Link href='/shop' className={primaryButton}>
                            Shop the paper
                            <ArrowRight className='size-4' strokeWidth={1.75} />
                        </Link>
                        <Link href='/workshops' className={ghostButton}>
                            Visit the workshop
                        </Link>
                    </div>
                </div>

                <div className='relative lg:col-span-7 lg:col-start-6 lg:pl-10'>
                    <div className='animate-in fade-in duration-[1800ms] ease-out motion-reduce:animate-none'>
                        <div className='relative md:ml-auto md:w-[88%] lg:w-full lg:rotate-[0.8deg]'>
                            <DeckleImage
                                src='/images/qogoz/hero-paper.jpg'
                                alt='Close up of a polished sheet of handmade mulberry paper, its torn edge soft with loose fibres'
                                sizes='(min-width: 1024px) 52vw, (min-width: 768px) 80vw, 100vw'
                                priority
                            />

                            <div aria-hidden='true' className='absolute -bottom-10 -left-4 size-28 sm:size-32 md:-left-12 lg:-left-16 lg:bottom-16 lg:size-36'>
                                <div className='relative size-full rounded-full bg-pomegranate shadow-[0_14px_30px_-12px_rgba(130,41,31,0.65)]'>
                                    <svg viewBox='0 0 120 120' className='absolute inset-0 size-full'>
                                        <defs>
                                            <path id='seal-ring' d='M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0' />
                                        </defs>
                                        <text fill='#f3ebdc' fontSize='10.5' letterSpacing='2' style={{ fontFamily: 'var(--font-heading)' }}>
                                            <textPath href='#seal-ring' textLength='272' lengthAdjust='spacing'>
                                                MULBERRY BARK &middot; RIVER WATER &middot; STONE &middot;
                                            </textPath>
                                        </text>
                                    </svg>
                                    <span className='wonk absolute inset-0 flex items-center justify-center font-heading text-[2.4rem] italic text-paper'>
                                        Q
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className='mt-14 text-right font-heading text-[0.95rem] italic text-bark-soft sm:mt-6 md:pr-2'>
                        Fig. 1. A polished sheet, its edge still soft with loose fibres.
                    </p>
                </div>
            </div>
        </section>
    )
}

export default HomeHero
